import { useCallback, useEffect, useRef, useState } from 'react';

import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { LoyaltyEngineId, LoyaltyOffer } from 'state/loyalty/types';
import { logger } from 'utils/logger';

import { useServiceOffers } from '../state/service-offers-context';

export function useLoyaltyServiceOffer({
  loyaltyEngineId,
  skip = false,
}: {
  loyaltyEngineId: LoyaltyEngineId;
  skip?: boolean;
}) {
  const { getOfferById } = useServiceOffers();
  const { loading: loyaltyUserIdLoading } = useLoyaltyUserId();

  const [selectedIncentive, setSelectedIncentive] = useState<LoyaltyOffer | undefined>(undefined);
  // Assume loading until the first resolution so the detail route shows its
  // loader rather than treating a not-yet-fetched offer as missing.
  const [loading, setLoading] = useState(!skip);

  const skipQueries = skip || !loyaltyEngineId || loyaltyUserIdLoading;

  // Needed because we await `getOfferById` and mirror the result into local
  // state instead of reading a `useQuery` result. Apollo can't discard stale
  // responses for state it doesn't own, so this guards against an out-of-order
  // resolution overwriting a newer request.
  const requestIdRef = useRef(0);

  const load = useCallback(
    async ({ forceNetwork = false }: { forceNetwork?: boolean } = {}) => {
      if (skipQueries) {
        // Nothing to fetch: invalidate any in-flight request and clear state so
        // a permanently-skipped render (e.g. a missing route param) dismisses
        // instead of hanging on the loader.
        requestIdRef.current += 1;
        setSelectedIncentive(undefined);
        setLoading(false);
        return;
      }
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      setLoading(true);
      try {
        const offer = await getOfferById(loyaltyEngineId, { forceNetwork });
        if (requestId === requestIdRef.current) {
          setSelectedIncentive(offer);
        }
      } catch (error) {
        // A rejected request (e.g. network failure) must clear loading and
        // surface as a missing offer so the detail modal reaches its
        // missing-offer dismissal instead of hanging on the loader forever.
        if (requestId === requestIdRef.current) {
          setSelectedIncentive(undefined);
          logger.warn({ error, message: 'Failed to load loyalty-service offer', loyaltyEngineId });
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [getOfferById, loyaltyEngineId, skipQueries]
  );

  // Force network on the detail route: the normalized cache is keyed only by
  // `loyaltyEngineId`, so a cache entry written under a previous store/service
  // mode could otherwise surface an offer that is unavailable in the current
  // context. The detail screen decides eligibility, so it must always evaluate
  // against the current context.
  useEffect(() => {
    void load({ forceNetwork: true });
  }, [load]);

  const refreshOffer = useCallback(async () => {
    await load({ forceNetwork: true });
  }, [load]);

  const isIncentivesLoading = !skip && (loyaltyUserIdLoading || loading);

  return {
    selectedIncentive,
    isIncentivesLoading,
    refreshOffer,
  };
}
